import { useEffect, useState, type JSX } from 'react';
import { fetchDashboard } from './api.js';
import type { DashboardPayload } from './dashboard-types.js';
import { Topbar } from './dashboard/Topbar.js';
import { PageHeader } from './dashboard/PageHeader.js';
import { ViewSwitcher, type ViewMode } from './dashboard/ViewSwitcher.js';
import { DimensionFilter } from './dashboard/DimensionFilter.js';
import { CurrentView } from './dashboard/CurrentView.js';
import { HistoryView } from './dashboard/HistoryView.js';

interface AppProps {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 選定可能型数ウォッチのトップページコンポーネント。styling-pattern-watch-api の /v1/dashboard を呼び、ロード/エラー状態を扱った上で Dashboard 内部コンポーネントへ委譲する
 * @graph-connects fetchDashboard [calls] ダッシュボード API クライアント呼び出し
 * @graph-connects Dashboard [calls] 取得済みペイロードをダッシュボード本体に渡す
 */
export function App(props: AppProps): JSX.Element {
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard({ baseUrl: props.baseUrl, fetchImpl: props.fetchImpl })
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [props.baseUrl, props.fetchImpl]);

  if (error !== null) {
    return (
      <div style={{ padding: 32 }}>
        <div
          style={{
            border: '1px solid #d99a9a',
            background: '#fbe6e6',
            color: '#c83232',
            padding: 16,
            borderRadius: 8,
          }}
        >
          {error}
        </div>
      </div>
    );
  }
  if (!data) {
    return <div style={{ padding: 32, color: '#5b6472' }}>読み込み中…</div>;
  }
  return <Dashboard data={data} baseUrl={props.baseUrl} fetchImpl={props.fetchImpl} />;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 取得済みダッシュボードペイロードを受け取り、Topbar / PageHeader / DimensionFilter / ViewSwitcher と現在/履歴ビューを組み合わせて描画する
 * @graph-connects Topbar [calls] グローバルヘッダ
 * @graph-connects PageHeader [calls] ページヘッダ
 * @graph-connects DimensionFilter [calls] 寸法フィルター
 * @graph-connects ViewSwitcher [calls] 現在/履歴の切替
 * @graph-connects CurrentView [calls] 現在スナップショットビュー
 * @graph-connects HistoryView [calls] 時系列履歴ビュー
 */
function Dashboard(props: {
  data: DashboardPayload;
  baseUrl: string;
  fetchImpl?: typeof fetch;
}): JSX.Element {
  const { data } = props;
  const [view, setView] = useState<ViewMode>('current');
  const [dimensionRanges, setDimensionRanges] = useState<Record<string, [number, number]>>(() => {
    const init: Record<string, [number, number]> = {};
    for (const d of data.dimensions) init[d.key] = [d.min, d.max];
    return init;
  });

  return (
    <>
      <Topbar user={data.user} />
      <div className="container">
        <PageHeader lastUpdated={data.lastUpdated} nextUpdate={data.nextUpdate} />
        <DimensionFilter
          dimensions={data.dimensions}
          categories={data.categories}
          catDim={data.catDim}
          ranges={dimensionRanges}
          onChange={setDimensionRanges}
        />
        <ViewSwitcher view={view} onChange={setView} />
        {view === 'current' ? (
          <CurrentView data={data} dimensionRanges={dimensionRanges} />
        ) : (
          <HistoryView data={data} baseUrl={props.baseUrl} fetchImpl={props.fetchImpl} />
        )}
      </div>
    </>
  );
}
